import React, { forwardRef } from 'react';

const Input = forwardRef(({
  type = 'text',
  placeholder = '',
  value,
  onChange,
  name,
  id,
  label,
  error,
  invalid = false,
  disabled = false,
  readOnly = false,
  required = false,
  className = '',
  minValue = 1, // Default minimum value for number inputs
  ...props
}, ref) => {
  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (e) => {
    if (!onChange) return;

    let newValue = e.target.value;

    // Prevent leading spaces for text inputs
    if (type === 'text' || type === 'email' || type === 'tel' || type === 'password') {
      // Remove leading spaces
      if (newValue.startsWith(' ')) {
        newValue = newValue.trimStart();
      }
    }

    // Prevent numbers less than minValue for number inputs
    if (type === 'number') {
      // Allow empty string for clearing
      if (newValue === '') {
        // Empty value is allowed
      } else {
        const numValue = parseFloat(newValue);
        // If minValue is 0, allow values > 0 (like 0.12) but not exactly 0
        // Otherwise, use standard >= minValue check
        if (isNaN(numValue)) {
          return; // Don't update if not a valid number
        }
        if (minValue === 0) {
          // For minValue 0, we want > 0 (not >= 0)
          if (numValue <= 0) {
            return; // Don't update if <= 0
          }
        } else {
          // For other minValues, use >= check
          if (numValue < minValue) {
            return; // Don't update if less than minValue
          }
        }
      }
    }

    // If value changed, create a new event object with the modified value
    if (newValue !== e.target.value) {
      const modifiedEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue,
        },
        currentTarget: {
          ...e.currentTarget,
          value: newValue,
        },
      };
      onChange(modifiedEvent);
    } else {
      // Value unchanged, pass through original event
      onChange(e);
    }
  };

  return (
    <div className={`input-wrapper ${className}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        id={inputId}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        className={`input-field ${(error || invalid) ? 'input-error' : ''}`.trim()}
        {...props}
      />
      <div className={`error-message-container ${error ? 'has-error' : ''}`}>
        {error && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
